import { useMutation, UseMutationResult } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
	createAuthSignupEmail,
	createAuthSignupOauth,
	createAuthSigninEmail,
	createAuthSigninOauth,
	createAuthSigninWebOauth,
	createAuthSigninWebEmail,
	createAuthSwitchWeb,
	createAuthBusinessOwner,
	createAuthBusinessAdmin,
	removeAuthBusinessAdmin,
	removeAuthBusinessAdminId,
	createAuthInternalTokenValidate,
	createAuthInternalTokenRefresh,
	createAuthEmailSend,
	createAuthEmailVerify,
	createAuthWebRefresh,
	modifyAuthPassword,
	createAuthCmsBusinessAccount,
	createAuthInternalCheckBusiness,
	createAuthDevice,
	createAuthDeviceApprove,
	createAuthDeviceToken,
	createAuthAppleAccountCallback,
} from './authAPI';
import {
	AuthController_signupEmail_Body,
	AuthController_signupEmail_Response,
	AuthController_signupOauth_Body,
	AuthController_signupOauth_Response,
	AuthController_signinEmail_Body,
	AuthController_signinEmail_Response,
	AuthController_signinOauth_Body,
	AuthController_signinOauth_Response,
	AuthController_signinOauthWeb_Body,
	AuthController_signinOauthWeb_Response,
	AuthController_signinEmailWeb_Body,
	AuthController_signinEmailWeb_Response,
	AuthController_getWebToken_Body,
	AuthController_getWebToken_Response,
	AuthController_grantBusinessAccountOwner_Params,
	AuthController_grantBusinessAccountOwner_Body,
	AuthController_grantBusinessAccountOwner_Response,
	AuthController_grantBusinessAccountAdmin_Params,
	AuthController_grantBusinessAccountAdmin_Body,
	AuthController_grantBusinessAccountAdmin_Response,
	AuthController_selfReleaseBusinessAccountAdmin_Body,
	AuthController_selfReleaseBusinessAccountAdmin_Response,
	AuthController_releaseBusinessAccountAdmin_Params,
	AuthController_releaseBusinessAccountAdmin_Response,
	AuthController_validateAccessToken_Body,
	AuthController_validateAccessToken_Response,
	AuthController_refreshToken_Body,
	AuthController_refreshToken_Response,
	AuthController_sendEmail_Body,
	AuthController_sendEmail_Response,
	AuthController_verifyEmail_Body,
	AuthController_verifyEmail_Response,
	AuthController_webRefresh_Response,
	AuthController_resetPassword_Body,
	AuthController_resetPassword_Response,
	AuthController_makeBusinessAccount_Body,
	AuthController_makeBusinessAccount_Response,
	AuthController_checkBusinessLinked_Body,
	AuthController_checkBusinessLinked_Response,
	AuthController_requestDeviceAutorization_Body,
	AuthController_requestDeviceAutorization_Response,
	AuthController_approveDevice_Params,
	AuthController_approveDevice_Response,
	AuthController_requestDeviceToken_Body,
	AuthController_requestDeviceToken_Response,
	AuthController_handleAppleAccountCallback_Body,
	AuthController_handleAppleAccountCallback_Response,
} from '@/domains/auth/types/validated';

export const useCreateAuthSignupEmailMutation = (): UseMutationResult<
	AuthController_signupEmail_Response,
	Error,
	AuthController_signupEmail_Body
> => {
	return useMutation({
		mutationFn: async (variables: AuthController_signupEmail_Body) => {
			return createAuthSignupEmail(variables);
		},
		onSuccess: (result: AuthController_signupEmail_Response) => {
			toast.success('Operation completed successfully');
		},
		onError: (error: Error) => {
			const errorMessage = error.message || 'Mutation failed';
			toast.error(`Operation Error: ${errorMessage}`);
		},
	});
};

export const useCreateAuthSignupOauthMutation = (): UseMutationResult<
	AuthController_signupOauth_Response,
	Error,
	AuthController_signupOauth_Body
> => {
	return useMutation({
		mutationFn: async (variables: AuthController_signupOauth_Body) => {
			return createAuthSignupOauth(variables);
		},
		onSuccess: (result: AuthController_signupOauth_Response) => {
			toast.success('Operation completed successfully');
		},
		onError: (error: Error) => {
			const errorMessage = error.message || 'Mutation failed';
			toast.error(`Operation Error: ${errorMessage}`);
		},
	});
};

export const useCreateAuthSigninEmailMutation = (): UseMutationResult<
	AuthController_signinEmail_Response,
	Error,
	AuthController_signinEmail_Body
> => {
	return useMutation({
		mutationFn: async (variables: AuthController_signinEmail_Body) => {
			return createAuthSigninEmail(variables);
		},
		onSuccess: (result: AuthController_signinEmail_Response) => {
			toast.success('Operation completed successfully');
		},
		onError: (error: Error) => {
			const errorMessage = error.message || 'Mutation failed';
			toast.error(`Operation Error: ${errorMessage}`);
		},
	});
};

export const useCreateAuthSigninOauthMutation = (): UseMutationResult<
	AuthController_signinOauth_Response,
	Error,
	AuthController_signinOauth_Body
> => {
	return useMutation({
		mutationFn: async (variables: AuthController_signinOauth_Body) => {
			return createAuthSigninOauth(variables);
		},
		onSuccess: (result: AuthController_signinOauth_Response) => {
			toast.success('Operation completed successfully');
		},
		onError: (error: Error) => {
			const errorMessage = error.message || 'Mutation failed';
			toast.error(`Operation Error: ${errorMessage}`);
		},
	});
};

export const useCreateAuthSigninWebOauthMutation = (): UseMutationResult<
	AuthController_signinOauthWeb_Response,
	Error,
	AuthController_signinOauthWeb_Body
> => {
	return useMutation({
		mutationFn: async (variables: AuthController_signinOauthWeb_Body) => {
			return createAuthSigninWebOauth(variables);
		},
		onSuccess: (result: AuthController_signinOauthWeb_Response) => {
			toast.success('Operation completed successfully');
		},
		onError: (error: Error) => {
			const errorMessage = error.message || 'Mutation failed';
			toast.error(`Operation Error: ${errorMessage}`);
		},
	});
};

export const useCreateAuthSigninWebEmailMutation = (): UseMutationResult<
	AuthController_signinEmailWeb_Response,
	Error,
	AuthController_signinEmailWeb_Body
> => {
	return useMutation({
		mutationFn: async (variables: AuthController_signinEmailWeb_Body) => {
			return createAuthSigninWebEmail(variables);
		},
		onSuccess: (result: AuthController_signinEmailWeb_Response) => {
			toast.success('Operation completed successfully');
		},
		onError: (error: Error) => {
			const errorMessage = error.message || 'Mutation failed';
			toast.error(`Operation Error: ${errorMessage}`);
		},
	});
};

export const useCreateAuthSwitchWebMutation = (): UseMutationResult<
	AuthController_getWebToken_Response,
	Error,
	AuthController_getWebToken_Body
> => {
	return useMutation({
		mutationFn: async (variables: AuthController_getWebToken_Body) => {
			return createAuthSwitchWeb(variables);
		},
		onSuccess: (result: AuthController_getWebToken_Response) => {
			toast.success('Operation completed successfully');
		},
		onError: (error: Error) => {
			const errorMessage = error.message || 'Mutation failed';
			toast.error(`Operation Error: ${errorMessage}`);
		},
	});
};

export const useCreateAuthBusinessOwnerMutation = (): UseMutationResult<
	AuthController_grantBusinessAccountOwner_Response,
	Error,
	{
		params: AuthController_grantBusinessAccountOwner_Params;
		requestBody: AuthController_grantBusinessAccountOwner_Body;
	}
> => {
	return useMutation({
		mutationFn: async (variables: {
			params: AuthController_grantBusinessAccountOwner_Params;
			requestBody: AuthController_grantBusinessAccountOwner_Body;
		}) => {
			return createAuthBusinessOwner(variables.params, variables.requestBody);
		},
		onSuccess: (result: AuthController_grantBusinessAccountOwner_Response) => {
			toast.success('Operation completed successfully');
		},
		onError: (error: Error) => {
			const errorMessage = error.message || 'Mutation failed';
			toast.error(`Operation Error: ${errorMessage}`);
		},
	});
};

export const useCreateAuthBusinessAdminMutation = (): UseMutationResult<
	AuthController_grantBusinessAccountAdmin_Response,
	Error,
	{
		params: AuthController_grantBusinessAccountAdmin_Params;
		requestBody: AuthController_grantBusinessAccountAdmin_Body;
	}
> => {
	return useMutation({
		mutationFn: async (variables: {
			params: AuthController_grantBusinessAccountAdmin_Params;
			requestBody: AuthController_grantBusinessAccountAdmin_Body;
		}) => {
			return createAuthBusinessAdmin(variables.params, variables.requestBody);
		},
		onSuccess: (result: AuthController_grantBusinessAccountAdmin_Response) => {
			toast.success('Operation completed successfully');
		},
		onError: (error: Error) => {
			const errorMessage = error.message || 'Mutation failed';
			toast.error(`Operation Error: ${errorMessage}`);
		},
	});
};

export const useRemoveAuthBusinessAdminMutation = (): UseMutationResult<
	AuthController_selfReleaseBusinessAccountAdmin_Response,
	Error,
	AuthController_selfReleaseBusinessAccountAdmin_Body
> => {
	return useMutation({
		mutationFn: async (variables: AuthController_selfReleaseBusinessAccountAdmin_Body) => {
			return removeAuthBusinessAdmin(variables);
		},
		onSuccess: (result: AuthController_selfReleaseBusinessAccountAdmin_Response) => {
			toast.success('Operation completed successfully');
		},
		onError: (error: Error) => {
			const errorMessage = error.message || 'Mutation failed';
			toast.error(`Operation Error: ${errorMessage}`);
		},
	});
};

export const useRemoveAuthBusinessAdminIdMutation = (): UseMutationResult<
	AuthController_releaseBusinessAccountAdmin_Response,
	Error,
	AuthController_releaseBusinessAccountAdmin_Params
> => {
	return useMutation({
		mutationFn: async (variables: AuthController_releaseBusinessAccountAdmin_Params) => {
			return removeAuthBusinessAdminId(variables);
		},
		onSuccess: (result: AuthController_releaseBusinessAccountAdmin_Response) => {
			toast.success('Operation completed successfully');
		},
		onError: (error: Error) => {
			const errorMessage = error.message || 'Mutation failed';
			toast.error(`Operation Error: ${errorMessage}`);
		},
	});
};

export const useCreateAuthInternalTokenValidateMutation = (): UseMutationResult<
	AuthController_validateAccessToken_Response,
	Error,
	AuthController_validateAccessToken_Body
> => {
	return useMutation({
		mutationFn: async (variables: AuthController_validateAccessToken_Body) => {
			return createAuthInternalTokenValidate(variables);
		},
		onSuccess: (result: AuthController_validateAccessToken_Response) => {
			toast.success('Operation completed successfully');
		},
		onError: (error: Error) => {
			const errorMessage = error.message || 'Mutation failed';
			toast.error(`Operation Error: ${errorMessage}`);
		},
	});
};

export const useCreateAuthInternalTokenRefreshMutation = (): UseMutationResult<
	AuthController_refreshToken_Response,
	Error,
	AuthController_refreshToken_Body
> => {
	return useMutation({
		mutationFn: async (variables: AuthController_refreshToken_Body) => {
			return createAuthInternalTokenRefresh(variables);
		},
		onSuccess: (result: AuthController_refreshToken_Response) => {
			toast.success('Operation completed successfully');
		},
		onError: (error: Error) => {
			const errorMessage = error.message || 'Mutation failed';
			toast.error(`Operation Error: ${errorMessage}`);
		},
	});
};

export const useCreateAuthEmailSendMutation = (): UseMutationResult<
	AuthController_sendEmail_Response,
	Error,
	AuthController_sendEmail_Body
> => {
	return useMutation({
		mutationFn: async (variables: AuthController_sendEmail_Body) => {
			return createAuthEmailSend(variables);
		},
		onSuccess: (result: AuthController_sendEmail_Response) => {
			toast.success('Operation completed successfully');
		},
		onError: (error: Error) => {
			const errorMessage = error.message || 'Mutation failed';
			toast.error(`Operation Error: ${errorMessage}`);
		},
	});
};

export const useCreateAuthEmailVerifyMutation = (): UseMutationResult<
	AuthController_verifyEmail_Response,
	Error,
	AuthController_verifyEmail_Body
> => {
	return useMutation({
		mutationFn: async (variables: AuthController_verifyEmail_Body) => {
			return createAuthEmailVerify(variables);
		},
		onSuccess: (result: AuthController_verifyEmail_Response) => {
			toast.success('Operation completed successfully');
		},
		onError: (error: Error) => {
			const errorMessage = error.message || 'Mutation failed';
			toast.error(`Operation Error: ${errorMessage}`);
		},
	});
};

export const useCreateAuthWebRefreshMutation = (): UseMutationResult<
	AuthController_webRefresh_Response,
	Error,
	void
> => {
	return useMutation({
		mutationFn: async () => {
			return createAuthWebRefresh();
		},
		onSuccess: (result: AuthController_webRefresh_Response) => {
			toast.success('Operation completed successfully');
		},
		onError: (error: Error) => {
			const errorMessage = error.message || 'Mutation failed';
			toast.error(`Operation Error: ${errorMessage}`);
		},
	});
};

export const useModifyAuthPasswordMutation = (): UseMutationResult<
	AuthController_resetPassword_Response,
	Error,
	AuthController_resetPassword_Body
> => {
	return useMutation({
		mutationFn: async (variables: AuthController_resetPassword_Body) => {
			return modifyAuthPassword(variables);
		},
		onSuccess: (result: AuthController_resetPassword_Response) => {
			toast.success('Operation completed successfully');
		},
		onError: (error: Error) => {
			const errorMessage = error.message || 'Mutation failed';
			toast.error(`Operation Error: ${errorMessage}`);
		},
	});
};

export const useCreateAuthCmsBusinessAccountMutation = (): UseMutationResult<
	AuthController_makeBusinessAccount_Response,
	Error,
	AuthController_makeBusinessAccount_Body
> => {
	return useMutation({
		mutationFn: async (variables: AuthController_makeBusinessAccount_Body) => {
			return createAuthCmsBusinessAccount(variables);
		},
		onSuccess: (result: AuthController_makeBusinessAccount_Response) => {
			toast.success('Operation completed successfully');
		},
		onError: (error: Error) => {
			const errorMessage = error.message || 'Mutation failed';
			toast.error(`Operation Error: ${errorMessage}`);
		},
	});
};

export const useCreateAuthInternalCheckBusinessMutation = (): UseMutationResult<
	AuthController_checkBusinessLinked_Response,
	Error,
	AuthController_checkBusinessLinked_Body
> => {
	return useMutation({
		mutationFn: async (variables: AuthController_checkBusinessLinked_Body) => {
			return createAuthInternalCheckBusiness(variables);
		},
		onSuccess: (result: AuthController_checkBusinessLinked_Response) => {
			toast.success('Operation completed successfully');
		},
		onError: (error: Error) => {
			const errorMessage = error.message || 'Mutation failed';
			toast.error(`Operation Error: ${errorMessage}`);
		},
	});
};

export const useCreateAuthDeviceMutation = (): UseMutationResult<
	AuthController_requestDeviceAutorization_Response,
	Error,
	AuthController_requestDeviceAutorization_Body
> => {
	return useMutation({
		mutationFn: async (variables: AuthController_requestDeviceAutorization_Body) => {
			return createAuthDevice(variables);
		},
		onSuccess: (result: AuthController_requestDeviceAutorization_Response) => {
			toast.success('Operation completed successfully');
		},
		onError: (error: Error) => {
			const errorMessage = error.message || 'Mutation failed';
			toast.error(`Operation Error: ${errorMessage}`);
		},
	});
};

export const useCreateAuthDeviceApproveMutation = (): UseMutationResult<
	AuthController_approveDevice_Response,
	Error,
	AuthController_approveDevice_Params
> => {
	return useMutation({
		mutationFn: async (variables: AuthController_approveDevice_Params) => {
			return createAuthDeviceApprove(variables);
		},
		onSuccess: (result: AuthController_approveDevice_Response) => {
			toast.success('Operation completed successfully');
		},
		onError: (error: Error) => {
			const errorMessage = error.message || 'Mutation failed';
			toast.error(`Operation Error: ${errorMessage}`);
		},
	});
};

export const useCreateAuthDeviceTokenMutation = (): UseMutationResult<
	AuthController_requestDeviceToken_Response,
	Error,
	AuthController_requestDeviceToken_Body
> => {
	return useMutation({
		mutationFn: async (variables: AuthController_requestDeviceToken_Body) => {
			return createAuthDeviceToken(variables);
		},
		onSuccess: (result: AuthController_requestDeviceToken_Response) => {
			toast.success('Operation completed successfully');
		},
		onError: (error: Error) => {
			const errorMessage = error.message || 'Mutation failed';
			toast.error(`Operation Error: ${errorMessage}`);
		},
	});
};

export const useCreateAuthAppleAccountCallbackMutation = (): UseMutationResult<
	AuthController_handleAppleAccountCallback_Response,
	Error,
	AuthController_handleAppleAccountCallback_Body
> => {
	return useMutation({
		mutationFn: async (variables: AuthController_handleAppleAccountCallback_Body) => {
			return createAuthAppleAccountCallback(variables);
		},
		onSuccess: (result: AuthController_handleAppleAccountCallback_Response) => {
			toast.success('Operation completed successfully');
		},
		onError: (error: Error) => {
			const errorMessage = error.message || 'Mutation failed';
			toast.error(`Operation Error: ${errorMessage}`);
		},
	});
};
